
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { storage } from '../services/storage';
import { t } from '../services/i18n';
import { fileToBase64Compressed } from '../services/imageUtils';
import { 
  ArrowLeft, Calendar, MapPin, Droplets, Sun, Sprout, 
  Wind, Bell, Info, Image as ImageIcon, X, Maximize2, Trash2, AlertTriangle, Edit, Scissors, Camera, Share2,
  Loader2, CheckCircle2, AlertCircle, FlaskConical, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Activity, ShieldAlert,
  Plus, BellOff, AlarmClock, ExternalLink, Clock, Map as MapIcon, CalendarDays, RefreshCw, GripVertical
} from 'lucide-react';
import { Plant, PlantStatus, Reminders, PlantClassification, PlantKingdom, SunExposure } from '../types';
import { MapPicker } from '../components/MapPicker';

// Lightbox
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

/**
 * Componente de Imagem Otimizada com Placeholder e Efeito Fade
 */
const OptimizedPlantImage: React.FC<{ src: string; alt: string; className?: string; onClick?: () => void }> = ({ src, alt, className, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  return (
    <div className={`relative overflow-hidden bg-raizama-surface ${className}`} onClick={onClick}>
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center animate-pulse bg-white/5">
          <ImageIcon className="text-raizama-border/20" size={24} />
        </div>
      )}
      {hasError ? (
        <div className="absolute inset-0 flex items-center justify-center bg-red-900/10">
          <AlertCircle className="text-red-500/40" size={24} />
        </div>
      ) : (
        <img 
          src={src} 
          alt={alt} 
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
          className={`w-full h-full object-cover transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} 
        />
      )}
    </div>
  );
};

export const PlantDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [plant, setPlant] = useState<Plant | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isUpdatingReminder, setIsUpdatingReminder] = useState(false);
  const [isUpdatingWatering, setIsUpdatingWatering] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'care' | 'health' | 'photos'>('info');
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Modais de Exclusão
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [photoToDelete, setPhotoToDelete] = useState<string | null>(null);
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
  
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const translateEnum = (val: string | undefined) => {
    if (!val) return '-';
    const mapping: Record<string, string> = {
      [PlantClassification.SUCCULENTS_CACTI]: t('enum_succulents_cacti'),
      [PlantClassification.TROPICAL_FOLIAGE]: t('enum_tropical_foliage'),
      [PlantClassification.ORCHIDS]: t('enum_orchids'),
      [PlantClassification.TREES_SHRUBS]: t('enum_trees_shrubs'),
      [PlantClassification.AQUATIC]: t('enum_aquatic'),
      [PlantClassification.EDIBLE_GARDEN]: t('enum_edible_garden'),
      [PlantClassification.CAUDICIFORM]: t('enum_caudiciform'),
      [PlantClassification.MEDICINAL]: t('enum_medicinal'),
      [PlantKingdom.BRIOFITAS]: t('enum_briofitas'),
      [PlantKingdom.PTERIDOFITAS]: t('enum_pteridofitas'),
      [PlantKingdom.GIMNOSPERMAS]: t('enum_gimnospermas'),
      [PlantKingdom.ANGIOSPERMAS]: t('enum_angiospermas'),
      [PlantStatus.HEALTHY]: t('enum_healthy'),
      [PlantStatus.OBSERVATION]: t('enum_observation'),
      [PlantStatus.SICK]: t('enum_sick'),
      [SunExposure.FULL_SUN]: t('enum_full_sun'),
      [SunExposure.PARTIAL_SHADE]: t('enum_partial_shade'),
      [SunExposure.SHADE]: t('enum_shade'),
      'comprada': t('details_origin_bought'),
      'coletada': t('details_origin_collected'),
      'cultivada': t('details_origin_cultivated'),
      'clone': t('details_origin_clone'),
      'semeio': t('details_origin_seed'),
      'presente': t('details_origin_gift'),
    };
    return mapping[val] || val;
  };

  useEffect(() => {
    const fetchPlant = async () => {
      if (id) {
        setLoading(true);
        try {
          const data = await storage.getPlantById(id);
          setPlant(data);
        } catch (err: any) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchPlant();
  }, [id]);

  // Reordenação por Drag and Drop
  const handleDragStart = (idx: number) => {
    setDraggedIdx(idx);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (targetIdx: number) => {
    if (draggedIdx === null || draggedIdx === targetIdx || !plant) return;
    
    const newImages = [...plant.imagens];
    const [draggedImage] = newImages.splice(draggedIdx, 1);
    newImages.splice(targetIdx, 0, draggedImage);
    
    setPlant({ ...plant, imagens: newImages });
    setDraggedIdx(null);
    
    try {
      await storage.updatePlant(plant.id, { imagens: newImages });
    } catch (err) {
      console.error("Erro ao salvar nova ordem das fotos:", err);
    }
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!plant) return;

    const statsText = [
      `🌿 *${plant.nomeComum}* (${plant.nomeCientifico || '-'})`,
      `🏷️ Categoria: ${translateEnum(plant.categoria)}`,
      `📍 Local: ${plant.localizacao?.descricao || plant.origem || '-'}`,
      `📅 Adquirida em: ${new Date(plant.dataAquisicao).toLocaleDateString('pt-BR')}`,
      `\nCuido das minhas plantas com o app Raizama Garden CARE!`
    ].join('\n');

    let shareData: any = {
      title: `Raizama Garden: ${plant.nomeComum}`,
      text: statsText,
      url: window.location.href,
    };

    if (plant.imagens && plant.imagens.length > 0) {
      try {
        const imageUrl = plant.imagens[0];
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const file = new File([blob], `planta-${plant.id}.jpg`, { type: blob.type || 'image/jpeg' });
        
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          shareData.files = [file];
        }
      } catch (err) {
        console.warn("Não foi possível processar a imagem para compartilhamento:", err);
      }
    }

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        const textToCopy = `${shareData.text}\n${shareData.url}`;
        await navigator.clipboard.writeText(textToCopy);
        alert(t('details_share_copied') || "Informações copiadas para a área de transferência!");
      }
    } catch (err) { 
      console.error("Erro ao compartilhar:", err); 
    }
  };

  const handleImageCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && id) {
      const file = e.target.files[0];
      setIsCapturing(true);
      try {
        const compressedBase64 = await fileToBase64Compressed(file);
        const newUrl = await storage.uploadPlantImage(id, compressedBase64);
        setPlant(prev => {
          if (!prev) return undefined;
          const updatedImages = prev.imagens ? [...prev.imagens, newUrl] : [newUrl];
          return { ...prev, imagens: updatedImages };
        });
        setActiveTab('photos'); 
        setTimeout(() => {
          setCurrentImageIndex(plant?.imagens ? plant.imagens.length : 0);
        }, 100);
      } catch (err) {
        console.error("Erro ao fazer upload da captura:", err);
      } finally {
        setIsCapturing(false);
        if (cameraInputRef.current) cameraInputRef.current.value = '';
        if (galleryInputRef.current) galleryInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async () => {
    if (!plant || !id || !photoToDelete || isDeletingPhoto) return;
    
    setIsDeletingPhoto(true);
    try {
      const updatedImages = plant.imagens.filter(img => img !== photoToDelete);
      await storage.updatePlant(id, { imagens: updatedImages });
      setPlant(prev => prev ? { ...prev, imagens: updatedImages } : undefined);
      
      if (isGalleryOpen) {
        if (updatedImages.length === 0) {
          setIsGalleryOpen(false);
        } else {
          setCurrentImageIndex(prev => Math.min(prev, updatedImages.length - 1));
        }
      }
      setPhotoToDelete(null);
    } catch (err) {
      console.error("Erro ao excluir foto:", err);
    } finally {
      setIsDeletingPhoto(false);
    }
  };

  const toggleReminder = async (key: keyof Reminders) => {
    if (!plant || !id || isUpdatingReminder) return;
    
    setIsUpdatingReminder(true);
    const newStatus = !plant.reminders?.[key];
    const updatedReminders = { ...(plant.reminders || { watering: false, fertilizing: false, pruning: false, repotting: false }), [key]: newStatus };
    
    try {
      await storage.updatePlant(id, { reminders: updatedReminders });
      setPlant(prev => prev ? { ...prev, reminders: updatedReminders } : undefined);
    } catch (err) {
      console.error("Erro ao atualizar lembrete:", err);
    } finally {
      setIsUpdatingReminder(false);
    }
  };

  const updateWateringDate = async () => {
    if (!plant || !id || isUpdatingWatering) return;
    setIsUpdatingWatering(true);
    const now = new Date().toISOString();
    try {
      await storage.updatePlant(id, { ultimaRega: now });
      setPlant(prev => prev ? { ...prev, ultimaRega: now } : undefined);
    } catch (err) {
      console.error("Erro ao registrar rega:", err);
    } finally {
      setIsUpdatingWatering(false);
    }
  };

  const handleScheduleCalendar = (key: keyof Reminders) => {
    if (!plant) return;

    const titles: Record<keyof Reminders, string> = {
      watering: `🌿 ALARME: ${t('details_care_watering')} ${plant.nomeComum}`,
      fertilizing: `🧪 ALARME: ${t('details_health_medication')} ${plant.nomeComum}`,
      pruning: `${t('details_care_alarm_title_pruning')} ${plant.nomeComum}`,
      repotting: `${t('details_care_alarm_title_repotting')} ${plant.nomeComum}`
    };

    const details: Record<keyof Reminders, string> = {
      watering: `${t('details_care_alarm_prefix_manejo')}: ${plant.planoManejo?.rega || t('details_care_check_humidity')}.`,
      fertilizing: `${t('details_care_alarm_prefix_adubo')}: ${plant.planoManejo?.tipoAdubo || t('details_care_nutrients')}.`,
      pruning: `${t('details_care_alarm_prefix_poda')}: ${plant.planoManejo?.poda || t('details_care_maintenance')}.`,
      repotting: t('details_care_repotting_desc')
    };

    const title = titles[key];
    const desc = `${details[key]} | Raizama Garden CARE`;
    
    const now = new Date();
    const startDate = new Date(now.getTime() + 60 * 60 * 1000);
    const endDate = new Date(startDate.getTime() + 15 * 60 * 1000);

    const formatICSDate = (date: Date) => {
      return date.toISOString().replace(/-|:|\.\d\d\d/g, "");
    };

    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "PRODID:-//Raizama Garden//CARE//PT",
      "BEGIN:VEVENT",
      `UID:${plant.id}-${key}-${now.getTime()}@raizamagarden.care`,
      `DTSTAMP:${formatICSDate(now)}`,
      `DTSTART:${formatICSDate(startDate)}`,
      `DTEND:${formatICSDate(endDate)}`,
      `SUMMARY:${title}`,
      `DESCRIPTION:${desc}`,
      "BEGIN:VALARM",
      "ACTION:DISPLAY",
      "DESCRIPTION:" + t('details_care_alarm_native_desc'),
      "TRIGGER:-PT0M",
      "END:VALARM",
      "END:VEVENT",
      "END:VCALENDAR"
    ].join("\n");

    const calendarUrl = `data:text/calendar;charset=utf8,${encodeURIComponent(icsContent)}`;
    window.location.assign(calendarUrl);
  };

  const handleDeletePlant = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await storage.deletePlant(id);
      navigate('/collection');
    } catch (err) {
      console.error("Erro ao deletar planta:", err);
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  if (loading) return <div className="flex flex-col items-center justify-center h-full pt-20"><Loader2 className="animate-spin text-raizama-action" size={40} /></div>;
  if (!plant) return <div className="flex flex-col items-center justify-center h-full pt-20 text-raizama-textSec"><p>{t('details_not_found')}</p></div>;

  const getStatusConfig = (status?: PlantStatus) => {
    switch (status) {
      case PlantStatus.SICK: return { bg: 'bg-red-600', icon: <AlertTriangle size={10} />, text: t('details_status_sick') };
      case PlantStatus.OBSERVATION: return { bg: 'bg-amber-400', icon: <AlertCircle size={10} />, text: t('details_status_observation') };
      default: return { bg: 'bg-emerald-600', icon: <CheckCircle2 size={10} />, text: t('details_status_healthy') };
    }
  };

  const statusConfig = getStatusConfig(plant.status);

  return (
    <div className="flex flex-col -m-4 pb-20 relative min-h-screen">
      <input type="file" accept="image/*" capture="environment" className="hidden" ref={cameraInputRef} onChange={handleImageCapture} />
      <input type="file" accept="image/*" className="hidden" ref={galleryInputRef} onChange={handleImageCapture} />

      <div className="relative h-72 w-full bg-gray-900">
        <div className="absolute inset-0 cursor-zoom-in overflow-hidden" onClick={() => { setCurrentImageIndex(0); setIsGalleryOpen(true); }}>
           <OptimizedPlantImage src={plant.imagens?.[0]} alt={plant.nomeComum} className="w-full h-full opacity-80" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-raizama-bg via-raizama-bg/20 to-transparent pointer-events-none" />
        
        <button onClick={(e) => { e.stopPropagation(); navigate(-1); }} className="absolute top-4 left-4 w-10 h-10 rounded-full bg-black/40 flex items-center justify-center text-white backdrop-blur-md z-10"><ArrowLeft size={24} /></button>
        
        <div className="absolute top-4 right-4 flex gap-2 z-10">
          <button onClick={handleShare} className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center text-raizama-action backdrop-blur-md"><Share2 size={20} /></button>
          <button onClick={(e) => { e.stopPropagation(); navigate(`/plant/${plant.id}/report`); }} className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center text-raizama-highlight backdrop-blur-md" title="Relatório de Vitalidade"><Activity size={20} /></button>
          <button onClick={(e) => { e.stopPropagation(); navigate(`/edit/${plant.id}`); }} className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center text-blue-400 backdrop-blur-md"><Edit size={20} /></button>
          <button onClick={(e) => { e.stopPropagation(); setIsDeleteModalOpen(true); }} className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center text-red-400 backdrop-blur-md"><Trash2 size={20} /></button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
          <div className="flex gap-2 mb-2">
            <span className="bg-raizama-action text-white text-[10px] font-bold px-2 py-1 rounded">{translateEnum(plant.categoria)}</span>
            <span className={`${statusConfig.bg} text-white text-[10px] font-black px-2 py-1 rounded flex items-center gap-1`}>{statusConfig.icon} {statusConfig.text}</span>
          </div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight mb-1">{plant.nomeComum}</h1>
          <p className="text-raizama-textSec italic text-sm">{plant.nomeCientifico}</p>
        </div>
      </div>

      <div className="bg-raizama-surface/80 p-1.5 gap-1.5 sticky top-16 z-20 backdrop-blur-md flex shadow-lg overflow-x-auto no-scrollbar">
        {['info', 'care', 'health', 'photos'].map((tab) => (
          <button 
            key={tab}
            onClick={() => setActiveTab(tab as any)} 
            className={`flex-1 min-w-[70px] py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all duration-300 ${activeTab === tab ? 'bg-raizama-action text-white shadow-md' : 'text-raizama-textSec hover:bg-white/5'}`}
          >
            {tab === 'info' ? t('details_tab_about') : tab === 'care' ? t('details_tab_care') : tab === 'health' ? t('details_tab_health') : t('details_tab_photos')}
          </button>
        ))}
      </div>

      <div className="p-6 space-y-6">
        {activeTab === 'info' && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-raizama-surface rounded-xl p-4 border border-raizama-border/20">
              <h3 className="text-raizama-highlight font-bold mb-4 flex items-center gap-2 uppercase text-[11px] tracking-wider"><Info size={18} /> {t('details_summary_title')}</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm border-b border-white/5 pb-2"><span className="text-raizama-textSec">{t('details_summary_common_name')}</span><span className="text-white font-medium">{plant.nomeComum}</span></div>
                <div className="flex justify-between text-sm border-b border-white/5 pb-2"><span className="text-raizama-textSec">{t('details_summary_scientific_name')}</span><span className="text-white font-medium italic">{plant.nomeCientifico}</span></div>
                {plant.genero && (
                  <div className="flex justify-between text-sm border-b border-white/5 pb-2"><span className="text-raizama-textSec">{t('details_summary_genus')}</span><span className="text-white font-medium">{plant.genero}</span></div>
                )}
                {plant.familia && (
                  <div className="flex justify-between text-sm border-b border-white/5 pb-2"><span className="text-raizama-textSec">{t('details_summary_family')}</span><span className="text-white font-medium">{plant.familia}</span></div>
                )}
                {plant.reinoPlantae && (
                  <div className="flex justify-between text-sm border-b border-white/5 pb-2"><span className="text-raizama-textSec">{t('details_summary_kingdom')}</span><span className="text-white font-medium">{translateEnum(plant.reinoPlantae)}</span></div>
                )}
                <div className="flex justify-between text-sm border-b border-white/5 pb-2"><span className="text-raizama-textSec">{t('details_summary_category')}</span><span className="text-white font-medium">{translateEnum(plant.categoria)}</span></div>
                <div className="flex justify-between text-sm border-b border-white/5 pb-2"><span className="text-raizama-textSec">{t('details_summary_status')}</span><span className={`font-black uppercase text-[10px] px-2 py-0.5 rounded ${statusConfig.bg} text-white`}>{translateEnum(plant.status)}</span></div>
                
                <div className="mt-4 pt-4 border-t border-raizama-border/10 space-y-3">
                  <div className="flex justify-between text-sm"><span className="text-raizama-textSec">{t('details_summary_origin')}</span><span className="text-white font-medium">{translateEnum(plant.origem)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-raizama-textSec">{t('details_summary_acquisition')}</span><span className="text-white font-medium">{new Date(plant.dataAquisicao).toLocaleDateString()}</span></div>
                  {plant.localizacao?.descricao && (
                    <div className="flex justify-between text-sm"><span className="text-raizama-textSec">{t('details_summary_location')}</span><span className="text-white font-medium">{plant.localizacao.descricao}</span></div>
                  )}
                </div>

                <div className="flex flex-col gap-2 mt-4">
                   <span className="text-[10px] font-bold text-raizama-textSec uppercase">{t('details_summary_notes')}</span>
                   <div className="text-white text-sm bg-black/20 p-4 rounded-xl border border-raizama-border/10 whitespace-pre-wrap leading-relaxed shadow-inner">
                     {plant.observacoes || t('details_summary_no_notes')}
                   </div>
                </div>
              </div>
            </div>

            {plant.localizacao?.latitude && plant.localizacao?.longitude ? (
              <div className="bg-raizama-surface rounded-xl p-4 border border-raizama-border/20 overflow-hidden">
                <h3 className="text-raizama-highlight font-bold mb-4 flex items-center gap-2 uppercase text-[11px] tracking-wider">
                  <MapPin size={18} /> {t('details_geo_title')}
                </h3>
                <div className="rounded-xl overflow-hidden border border-raizama-border/10 shadow-inner">
                  <MapPicker 
                    initialLat={plant.localizacao.latitude}
                    initialLng={plant.localizacao.longitude}
                    readOnly={true}
                    onLocationSelect={() => {}} 
                  />
                </div>
                <div className="mt-3 flex justify-between items-center text-[10px] text-raizama-textSec uppercase font-black px-1">
                  <div className="flex items-center gap-1"><MapIcon size={10} /> {t('details_geo_gps_active')}</div>
                  <div className="flex gap-3">
                    <span>LAT: {plant.localizacao.latitude.toFixed(6)}</span>
                    <span>LNG: {plant.localizacao.longitude.toFixed(6)}</span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {activeTab === 'care' && (
          <div className="space-y-6 animate-fade-in">
            <div className="grid grid-cols-2 gap-4">
               <div className="bg-raizama-surface p-4 rounded-2xl border border-raizama-border/20 text-center shadow-lg">
                  <Sun size={24} className="mx-auto text-orange-400 mb-2" />
                  <p className="text-[10px] text-raizama-textSec font-bold uppercase">{t('details_care_light')}</p>
                  <p className="text-xs font-bold text-white mt-1">{translateEnum(plant.planoManejo?.exposicaoSolar)}</p>
               </div>
               
               <div className="bg-raizama-surface p-4 rounded-2xl border border-raizama-border/20 text-center shadow-lg">
                  <Droplets size={24} className="mx-auto text-blue-400 mb-2" />
                  <p className="text-[10px] text-raizama-textSec font-bold uppercase">{t('details_care_watering')}</p>
                  <p className="text-xs font-bold text-white mt-1">{plant.planoManejo?.rega || t('form_reminder_inactive')}</p>
               </div>
            </div>

            <div className="bg-raizama-surface rounded-2xl p-5 border border-raizama-border/20 shadow-lg">
               <div className="flex items-center justify-between mb-4">
                  <h3 className="text-raizama-highlight font-black text-[11px] uppercase tracking-[0.2em] flex items-center gap-2">
                    <CalendarDays size={16} /> {t('details_care_last_watering')}
                  </h3>
                  <button 
                    onClick={updateWateringDate} 
                    disabled={isUpdatingWatering}
                    className="flex items-center gap-2 bg-raizama-action/10 hover:bg-raizama-action text-raizama-action hover:text-white px-3 py-1.5 rounded-full border border-raizama-action/20 transition-all active:scale-95 disabled:opacity-50"
                  >
                    {isUpdatingWatering ? <RefreshCw size={14} className="animate-spin" /> : <Droplets size={14} />}
                    <span className="text-[10px] font-black uppercase">{t('details_care_watered_now')}</span>
                  </button>
               </div>
               <div className="p-4 bg-black/20 rounded-xl border border-white/5 flex items-center justify-center">
                  {plant.ultimaRega ? (
                    <div className="flex flex-col items-center">
                      <p className="text-2xl font-black text-white">{new Date(plant.ultimaRega).toLocaleDateString()}</p>
                      <p className="text-[9px] text-raizama-textSec font-bold uppercase tracking-widest mt-1">{t('details_care_at')} {new Date(plant.ultimaRega).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-raizama-textSec italic">{t('details_care_no_watering')}</p>
                  )}
               </div>
            </div>

            <div className="bg-raizama-surface rounded-2xl p-5 border border-raizama-border/20 shadow-lg">
               <h3 className="text-raizama-highlight font-black text-[11px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                 <AlarmClock size={16} /> {t('details_care_alarms_title')}
               </h3>
               <div className="grid gap-3">
                  {[
                    { id: 'watering', label: t('form_reminder_watering'), icon: <Droplets size={16} />, color: 'text-blue-400' },
                    { id: 'fertilizing', label: t('form_reminder_fertilizing'), icon: <FlaskConical size={16} />, color: 'text-raizama-action' },
                    { id: 'pruning', label: t('form_reminder_pruning'), icon: <Scissors size={16} />, color: 'text-amber-400' },
                    { id: 'repotting', label: t('form_reminder_repotting'), icon: <Sprout size={16} />, color: 'text-emerald-400' }
                  ].map((rem) => {
                    const isActive = plant.reminders?.[rem.id as keyof Reminders];
                    return (
                      <div key={rem.id} className={`flex items-center justify-between p-3 rounded-xl border transition-all ${isActive ? 'bg-black/20 border-raizama-border/20 opacity-100' : 'bg-transparent border-dashed border-gray-800 opacity-40'}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center ${rem.color}`}>
                            {rem.icon}
                          </div>
                          <div>
                            <p className="text-xs font-bold text-white">{rem.label}</p>
                            <p className="text-[9px] text-raizama-textSec uppercase">{isActive ? t('details_care_reminder_active') : t('details_care_reminder_inactive')}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => toggleReminder(rem.id as keyof Reminders)}
                            disabled={isUpdatingReminder}
                            className={`p-2 rounded-lg transition-colors ${isActive ? 'text-raizama-action hover:bg-raizama-action/10' : 'text-gray-500 hover:text-white'}`}
                          >
                            {isUpdatingReminder ? <Loader2 size={18} className="animate-spin" /> : isActive ? <Bell size={18} /> : <BellOff size={18} />}
                          </button>
                          {isActive && (
                            <button 
                              onClick={() => handleScheduleCalendar(rem.id as keyof Reminders)}
                              className="p-2 bg-raizama-action text-white rounded-lg hover:brightness-110 active:scale-95 transition-all flex items-center gap-1.5 shadow-md shadow-raizama-action/20"
                            >
                              <Clock size={16} />
                              <span className="text-[9px] font-black uppercase">{t('details_care_schedule')}</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
               </div>
            </div>
          </div>
        )}

        {activeTab === 'health' && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-raizama-surface rounded-2xl p-5 border border-raizama-action/20 shadow-lg">
               <div className="flex items-center gap-3 mb-6">
                  <Activity size={24} className="text-raizama-action" />
                  <h3 className="text-raizama-highlight font-black text-[11px] uppercase tracking-wider">{t('details_health_title')}</h3>
               </div>
               <div className="space-y-5">
                  <div className="pl-6 border-l-2 border-raizama-action">
                    <h4 className="text-[10px] font-bold text-raizama-textSec uppercase tracking-widest mb-1 flex items-center gap-2"><FlaskConical size={12} className="text-raizama-action" /> {t('details_health_medication')}</h4>
                    <p className="text-sm text-white italic">{plant.tratamento?.medicacao || t('details_health_no_medication')}</p>
                  </div>
                  <div className="pl-6 border-l-2 border-orange-500">
                    <h4 className="text-[10px] font-bold text-raizama-textSec uppercase tracking-widest mb-1 flex items-center gap-2"><ShieldAlert size={12} className="text-orange-500" /> {t('details_health_pests')}</h4>
                    <p className="text-sm text-white italic">{plant.tratamento?.controlePragas || t('details_health_pests_clean')}</p>
                  </div>
               </div>
            </div>
          </div>
        )}

        {activeTab === 'photos' && (
          <div className="animate-fade-in space-y-4">
            <div className="flex items-center justify-between px-1">
              <span className="text-[10px] text-raizama-textSec font-black uppercase tracking-[0.2em]">{t('details_photos_reorder')}</span>
            </div>
            <div className="grid grid-cols-2 gap-4 pb-24">
              <div className="grid grid-rows-2 gap-4 aspect-square">
                  <button 
                    onClick={() => cameraInputRef.current?.click()} 
                    disabled={isCapturing}
                    className="w-full h-full rounded-2xl bg-white/5 border-2 border-dashed border-white/20 flex flex-col items-center justify-center gap-1 group hover:border-raizama-action hover:bg-raizama-action/10 transition-all active:scale-95 shadow-lg"
                  >
                    {isCapturing ? <Loader2 size={16} className="animate-spin text-raizama-action" /> : (
                      <>
                        <Camera size={20} className="text-raizama-textSec group-hover:text-raizama-action transition-colors" />
                        <span className="text-[8px] font-black text-raizama-textSec uppercase tracking-widest group-hover:text-white">{t('details_photos_camera')}</span>
                      </>
                    )}
                  </button>
                  <button 
                    onClick={() => galleryInputRef.current?.click()} 
                    disabled={isCapturing}
                    className="w-full h-full rounded-2xl bg-white/5 border-2 border-dashed border-white/20 flex flex-col items-center justify-center gap-1 group hover:border-raizama-action hover:bg-raizama-action/10 transition-all active:scale-95 shadow-lg"
                  >
                    {isCapturing ? <Loader2 size={16} className="animate-spin text-raizama-action" /> : (
                      <>
                        <ImageIcon size={20} className="text-raizama-textSec group-hover:text-raizama-action transition-colors" />
                        <span className="text-[8px] font-black text-raizama-textSec uppercase tracking-widest group-hover:text-white">{t('details_photos_gallery')}</span>
                      </>
                    )}
                  </button>
              </div>

              {plant.imagens?.map((img, idx) => (
                <div 
                  key={idx} 
                  draggable 
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(idx)}
                  className={`aspect-square rounded-2xl overflow-hidden border border-raizama-border/10 shadow-lg group relative cursor-move transition-all ${draggedIdx === idx ? 'opacity-40 scale-95 border-raizama-action' : 'opacity-100'}`}
                >
                  <OptimizedPlantImage src={img} alt={`${plant.nomeComum} ${idx + 1}`} className="w-full h-full group-hover:scale-105 transition-transform duration-500" onClick={() => { setCurrentImageIndex(idx); setIsGalleryOpen(true); }} />
                  
                  <div className="absolute top-2 left-2 bg-black/40 backdrop-blur-md p-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                    <GripVertical size={14} className="text-white" />
                  </div>

                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity pointer-events-none">
                    <Maximize2 size={24} className="text-white" />
                  </div>
                  
                  <button 
                    onClick={(e) => { e.stopPropagation(); setPhotoToDelete(img); }}
                    disabled={isDeletingPhoto}
                    className="absolute top-2 right-2 p-2 bg-red-600/80 text-white rounded-xl backdrop-blur-md opacity-0 group-hover:opacity-100 transition-all active:scale-90 z-10 hover:bg-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {isGalleryOpen && plant.imagens && (
        <Lightbox
          open={isGalleryOpen}
          close={() => setIsGalleryOpen(false)}
          index={currentImageIndex}
          slides={plant.imagens.map(src => ({ src }))}
          plugins={[Zoom, Thumbnails, Fullscreen]}
          zoom={{
            maxZoomPixelRatio: 3,
            zoomInMultiplier: 2,
            doubleTapDelay: 300,
            doubleClickDelay: 300,
            keyboardMoveDistance: 50,
            wheelZoomDistanceFactor: 100,
            pinchZoomDistanceFactor: 100,
            scrollToZoom: true,
          }}
          thumbnails={{
            position: "bottom",
            width: 120,
            height: 80,
            border: 1,
            gap: 16,
            padding: 4,
            showToggle: true,
          }}
        />
      )}

      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-[250] bg-black/80 backdrop-blur-md flex items-center justify-center p-6 animate-fade-in">
            <div className="bg-raizama-surface rounded-[2rem] p-8 w-full max-w-xs border border-red-500/30 shadow-2xl animate-scale-up text-center">
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 mx-auto mb-4">
                  <Trash2 size={32} className="animate-pulse" />
                </div>
                <h3 className="text-xl font-black text-white mb-2">{t('details_delete_confirm_title')}</h3>
                <p className="text-xs text-raizama-textSec mb-8 leading-relaxed">
                  {t('details_delete_confirm_desc').replace('{{count}}', plant.imagens.length.toString()).replace('{{name}}', plant.nomeComum)}
                </p>
                <div className="flex flex-col gap-3">
                    <button 
                      onClick={handleDeletePlant} 
                      disabled={isDeleting}
                      className="w-full py-4 bg-red-600 text-white rounded-2xl font-black hover:bg-red-700 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 uppercase text-[10px] tracking-widest shadow-lg shadow-red-600/20"
                    >
                      {isDeleting ? <Loader2 className="animate-spin" size={20} /> : <Trash2 size={16} />}
                      {t('details_delete_confirm_btn')}
                    </button>
                    <button 
                      onClick={() => setIsDeleteModalOpen(false)} 
                      disabled={isDeleting}
                      className="w-full py-4 bg-white/5 border border-white/10 text-white font-bold rounded-2xl hover:bg-white/10 transition-colors uppercase text-[10px] tracking-widest"
                    >
                      {t('details_delete_cancel_btn')}
                    </button>
                </div>
            </div>
        </div>
      )}

      {photoToDelete && (
        <div className="fixed inset-0 z-[250] bg-black/80 backdrop-blur-md flex items-center justify-center p-6 animate-fade-in">
            <div className="bg-raizama-surface rounded-[2rem] p-8 w-full max-w-xs border border-red-500/30 shadow-2xl animate-scale-up text-center">
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 mx-auto mb-4">
                  <ImageIcon size={32} />
                </div>
                <h3 className="text-xl font-black text-white mb-2">{t('details_photo_delete_title')}</h3>
                <p className="text-xs text-raizama-textSec mb-8 leading-relaxed">
                  {t('details_photo_delete_desc').replace('{{name}}', plant.nomeComum)}
                </p>
                <div className="flex flex-col gap-3">
                    <button 
                      onClick={handleDeletePhoto} 
                      disabled={isDeletingPhoto}
                      className="w-full py-4 bg-red-600 text-white rounded-2xl font-black hover:bg-red-700 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 uppercase text-[10px] tracking-widest shadow-lg shadow-red-600/20"
                    >
                      {isDeletingPhoto ? <Loader2 className="animate-spin" size={20} /> : <Trash2 size={16} />}
                      {t('details_photo_delete_btn')}
                    </button>
                    <button 
                      onClick={() => setPhotoToDelete(null)} 
                      disabled={isDeletingPhoto}
                      className="w-full py-4 bg-white/5 border border-white/10 text-white font-bold rounded-2xl hover:bg-white/10 transition-colors uppercase text-[10px] tracking-widest"
                    >
                      {t('details_delete_cancel_btn')}
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};
